import type { FallbackRender } from '@sentry/react';

/**
 * App-level error fallback - theme-agnostic
 *
 * Unlike SentryErrorFallback, this component:
 * - Does NOT use theme tokens (renders before ThemeProvider)
 * - Does NOT use Button component (requires useTheme)
 * - Uses inline styles and raw HTML for maximum reliability
 *
 * Used only for the root error boundary in App.tsx.
 * Feature-level boundaries should use SentryErrorFallback.
 */
export const AppErrorFallback: FallbackRender = ({
  error,
  eventId,
  resetError,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        backgroundColor: '#1f2937',
        color: '#ffffff',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: '28rem',
          padding: '2rem',
          backgroundColor: '#374151',
          borderRadius: '0.5rem',
          border: '1px solid #4b5563',
        }}
      >
        <h2
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '1rem',
            color: '#ef4444',
          }}
        >
          Something went wrong
        </h2>
        <p
          style={{
            fontSize: '0.875rem',
            marginBottom: '1rem',
            color: '#d1d5db',
          }}
        >
          {error instanceof Error ? error.message : 'An unexpected error occurred'}
        </p>
        {eventId && (
          <p
            style={{
              fontSize: '0.75rem',
              marginBottom: '1.5rem',
              color: '#9ca3af',
              fontFamily: 'monospace',
            }}
          >
            Error ID: {eventId}
          </p>
        )}
        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
          }}
        >
          <button
            onClick={resetError}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#ef4444',
              color: '#ffffff',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#dc2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ef4444';
            }}
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#4b5563',
              color: '#ffffff',
              border: '1px solid #6b7280',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#6b7280';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#4b5563';
            }}
          >
            Refresh Page
          </button>
        </div>
      </div>
    </div>
  );
};
